import type { SampleSpecRow } from './index';
import { sampleSheetColumnMetadata, type AcceptedRange, type RangeBounds } from './metadata';

const defaultBounds: RangeBounds = '[]';
const enDash = '\u2013';

export function inClosedRange(value: number, min: number, max: number): boolean {
  return value >= min && value <= max;
}

export function inOpenRange(value: number, min: number, max: number): boolean {
  return value > min && value < max;
}

export function gte(value: number, min: number): boolean {
  return value >= min;
}

export function lte(value: number, max: number): boolean {
  return value <= max;
}

export function formatNumericValue(value: number): string {
  if (!Number.isFinite(value)) {
    return String(value);
  }
  if (Number.isInteger(value)) {
    return value.toString();
  }
  const fixed = value.toFixed(2);
  return fixed.replace(/\.?0+$/, '');
}

export function describeAcceptedRange(
  fieldKey: keyof SampleSpecRow,
): { expectation: string; preferred?: string } | null {
  const range = sampleSheetColumnMetadata[fieldKey].acceptedRange;
  if (!range) {
    return null;
  }
  const expectation = formatRangeExpectation(range);
  const preferredTuple = range.preferred;
  const preferred = preferredTuple ? formatPreferredSpan(preferredTuple) : undefined;
  return { expectation, preferred };
}

export function formatRangeForTooltip(fieldKey: keyof SampleSpecRow): string | null {
  if (sampleSheetColumnMetadata[fieldKey].showRangeHint === false) {
    return null;
  }
  const details = describeAcceptedRange(fieldKey);
  if (!details) {
    return null;
  }
  const segments = [`Expected ${details.expectation}`];
  if (details.preferred) {
    segments.push(`(target ${details.preferred})`);
  }
  return `${segments.join(' ')}.`;
}

export function formatExpectedRange(fieldKey: keyof SampleSpecRow): string | null {
  const details = describeAcceptedRange(fieldKey);
  if (!details) {
    return null;
  }
  const segments = [`expected ${details.expectation}`];
  if (details.preferred) {
    segments.push(`(target ${details.preferred})`);
  }
  return segments.join(' ');
}

export function evaluateFieldSoftConstraint(fieldKey: keyof SampleSpecRow, numericValue: number): string[] {
  const range = sampleSheetColumnMetadata[fieldKey].acceptedRange;
  if (!range) {
    return [];
  }
  if (isWithinAcceptedRange(range, numericValue)) {
    return [];
  }
  const expected = formatExpectedRange(fieldKey);
  const valueText = formatNumericValue(numericValue);
  if (expected) {
    return [`${valueText} is outside ${expected}.`];
  }
  return [`${valueText} is outside the accepted range.`];
}

function isWithinAcceptedRange(range: AcceptedRange, value: number): boolean {
  switch (range.kind) {
    case 'between': {
      const bounds = range.bounds ?? defaultBounds;
      if (bounds === '[]') {
        return inClosedRange(value, range.min, range.max);
      }
      if (bounds === '()') {
        return inOpenRange(value, range.min, range.max);
      }
      if (bounds === '[)') {
        return value >= range.min && value < range.max;
      }
      return value > range.min && value <= range.max;
    }
    case 'gte':
      return gte(value, range.min);
    case 'gt':
      return value > range.min;
    case 'lte':
      return lte(value, range.max);
    case 'lt':
      return value < range.max;
    default:
      return true;
  }
}

function formatRangeExpectation(range: AcceptedRange): string {
  switch (range.kind) {
    case 'between': {
      const bounds = range.bounds ?? defaultBounds;
      if (bounds === '[]') {
        return `${formatNumericValue(range.min)}${enDash}${formatNumericValue(range.max)}`;
      }
      if (bounds === '()') {
        return `> ${formatNumericValue(range.min)} and < ${formatNumericValue(range.max)}`;
      }
      if (bounds === '[)') {
        return `\u2265 ${formatNumericValue(range.min)} and < ${formatNumericValue(range.max)}`;
      }
      return `> ${formatNumericValue(range.min)} and \u2264 ${formatNumericValue(range.max)}`;
    }
    case 'gte':
      return `\u2265 ${formatNumericValue(range.min)}`;
    case 'gt':
      return `> ${formatNumericValue(range.min)}`;
    case 'lte':
      return `\u2264 ${formatNumericValue(range.max)}`;
    case 'lt':
      return `< ${formatNumericValue(range.max)}`;
    default:
      return '';
  }
}

function formatPreferredSpan(span: [number, number]): string {
  return `${formatNumericValue(span[0])}${enDash}${formatNumericValue(span[1])}`;
}
